import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../shared';
import { adminAccess, adminTarget } from '../scripturia/access';

const LANGS = [
  { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
  { code: 'en', flag: '🇬🇧', name: 'English' },
  { code: 'ru', flag: '🇷🇺', name: 'Русский' },
  { code: 'es', flag: '🇪🇸', name: 'Español' },
  { code: 'fr', flag: '🇫🇷', name: 'Français' },
];

function LangToggle() {
  const [open, setOpen] = useState(false);
  const cur = (typeof window !== 'undefined' && window.__getSiteLang) ? window.__getSiteLang() : 'de';
  const curLang = LANGS.find(l => l.code === cur) || LANGS[0];
  const pick = (code) => { setOpen(false); if (code !== cur && window.__setSiteLang) window.__setSiteLang(code); };
  return (
    <div className="lang-toggle notranslate" translate="no" data-testid="lang-toggle" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
      <button type="button" data-testid="lang-current" aria-label="Sprache wählen" onClick={() => setOpen(o => !o)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(0,0,0,0.15)', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 999, padding: '5px 9px', cursor: 'pointer', lineHeight: 1 }}>
        <span style={{ fontSize: 18 }}>{curLang.flag}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ opacity: 0.7, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 4000 }} />
          <div data-testid="lang-menu" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 4001, background: '#1c1c22', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: 6, minWidth: 158, boxShadow: '0 12px 32px rgba(0,0,0,0.45)' }}>
            {LANGS.map(l => (
              <button key={l.code} type="button" data-testid={`lang-${l.code}`} onClick={() => pick(l.code)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', background: l.code === cur ? 'rgba(255,107,53,0.16)' : 'transparent', border: 'none', borderRadius: 8, padding: '9px 11px', cursor: 'pointer', color: l.code === cur ? '#ff8c42' : '#e5e5e7', fontSize: 14, fontWeight: l.code === cur ? 700 : 500 }}
                onMouseEnter={e => { if (l.code !== cur) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                onMouseLeave={e => { if (l.code !== cur) e.currentTarget.style.background = 'transparent'; }}>
                <span style={{ fontSize: 18, lineHeight: 1 }}>{l.flag}</span>{l.name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Header() {
  const { user, logout, inactivityCountdown } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [termineDropdownOpen, setTermineDropdownOpen] = useState(false);
  const dropdownTimeoutRef = React.useRef(null);

  const handleDropdownEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setTermineDropdownOpen(true);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setTermineDropdownOpen(false);
    }, 200); // 200ms Verzögerung
  };

  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          <Link to="/" className="logo">
            <img 
              src={user
                ? "https://customer-assets.emergentagent.com/job_holyfire-cms/artifacts/gv8j69e1_Holyfire-Ministry-Flamme-klein.png"
                : "https://customer-assets.emergentagent.com/job_a0fdad2e-c63a-456b-b460-11d85657e4bc/artifacts/pl5kzfg1_logo-holyfire-klein-h90px.png"}
              alt="Holyfire Ministry" 
              className={user ? "logo-image logo-image-icon" : "logo-image"}
              data-testid="header-logo"
            />
          </Link>
          
          <button 
            className="mobile-menu-btn" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            ☰
          </button>
          
          <nav className={`nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <Link to="/" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Start</Link>
            
            {/* Termine - direkt zum Kalender/Events */}
            <Link to="/events" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Termine</Link>
            <Link to="/shop" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Produkte & Kurse</Link>
            
            <Link to="/spenden" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Spenden</Link>
            <Link to="/bibelstudium" className="nav-link" data-testid="nav-bibelstudium" onClick={() => setMobileMenuOpen(false)} style={{ color: '#ff8c42', fontWeight: 700 }}>Bibelstudium</Link>

            {user ? (
              <>
                <Link to="/dashboard" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
                {adminAccess(user).isAnyAdmin && (
                  <Link to={adminTarget(user)} className="nav-link" data-testid="nav-admin-link" onClick={() => setMobileMenuOpen(false)}>Admin</Link>
                )}
                <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px'}}>
                  {inactivityCountdown < 300 && (
                    <span style={{fontSize: '11px', color: inactivityCountdown < 60 ? '#c62828' : '#ff9800', fontWeight: '600'}}>
                      {Math.floor(inactivityCountdown / 60)}:{(inactivityCountdown % 60).toString().padStart(2, '0')}
                    </span>
                  )}
                  <button onClick={() => {logout(); setMobileMenuOpen(false);}} className="btn-secondary">Abmelden</button>
                </div>
              </>
            ) : (
              <Link to="/login" className="btn-primary" onClick={() => setMobileMenuOpen(false)}>Anmelden</Link>
            )}

            <LangToggle />
          </nav>
        </div>
      </div>
    </header>
  );
}

// Home Page
